import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getApiUrl } from "../../api";

interface Notice {
  notice_id: number;
  author_id: number;
  title: string;
  created_at: string;
  thumbnail_url?: string;
}

interface NoticeListResponse {
  total: number;
  notices: Notice[];
}

export default function NoticeSection() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          getApiUrl("/marubit/api/notice/list?is_visible=1&limit=4")
        );
        
        if (!res.ok) {
          throw new Error("공지사항을 불러오는데 실패했습니다.");
        }
        
        const data: NoticeListResponse = await res.json();
        setNotices(data.notices || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "오류가 발생했습니다.");
        console.error("Error fetching notices:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = String(date.getDate()).padStart(2, "0");
    return { month, day };
  };

  if (loading) {
    return (
      <div className="bg-white py-9 pb-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="border-t border-gray-100 pt-9">
            <div className="flex justify-center items-center py-20">
              <span className="text-gray-400">로딩 중...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white py-9 pb-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="border-t border-gray-100 pt-9">
            <div className="flex justify-center items-center py-20">
              <span className="text-red-500">{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-9 pb-12">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="border-t border-gray-100 pt-9">

          {/* 헤더 */}
          <div className="flex justify-between items-center mb-5">
            <h2 className="m-0 text-[20px] font-bold text-[#5a4035] border-l-4 border-[#887267] pl-3">
              공지사항
            </h2>
            <Link
              to="/notice"
              className="text-[13px] text-gray-400 no-underline flex items-center gap-1 hover:text-[#887267] transition-colors duration-150"
            >
              전체보기
              <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.5}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Link>
          </div>

          {/* 목록 */}
          <div>
            {notices.length === 0 ? (
              <div className="flex justify-center items-center py-20">
                <span className="text-gray-400">등록된 공지사항이 없습니다.</span>
              </div>
            ) : (
              notices.map((notice, idx) => {
                const { month, day } = formatDate(notice.created_at);
                return (
                  <Link
                    key={notice.notice_id}
                    to={`/notice/${notice.notice_id}`}
                    className={[
                      "flex items-center gap-6 px-6 py-[18px] no-underline transition-colors duration-150 hover:bg-[#f5f2f0]",
                      idx < notices.length - 1 ? "border-b border-[#e8e0dc]" : "",
                      idx === 0 ? "rounded-t-[10px]" : "",
                      idx === notices.length - 1 ? "rounded-b-[10px]" : "",
                    ].join(" ")}
                  >
                    {/* 날짜 박스 */}
                    <div className="min-w-[52px] h-[52px] bg-[#f5f2f0] border-[1.5px] border-[#e8e0dc] rounded-[10px] flex flex-col items-center justify-center shrink-0">
                      <span className="text-[10px] text-[#887267] font-semibold leading-none">{month}</span>
                      <span className="text-[18px] text-[#887267] font-extrabold leading-snug">{day}</span>
                    </div>

                    {/* 제목 */}
                    <span className="text-[15px] text-[#333] font-medium overflow-hidden text-ellipsis whitespace-nowrap leading-[1.5]">
                      {notice.title}
                    </span>

                    {/* 화살표 */}
                    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#ccc" strokeWidth={2} className="shrink-0 ml-auto">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                );
              })
            )}
          </div>

        </div>
      </div>
    </div>
  );
}